import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getSheinWorkspaceToken,
  persistSheinWorkspaceToken,
  setSheinWorkspaceAuthRequiredHandler,
  sheinPlatformAPI,
} from '@/shein-platform/api/sheinPlatformApi';
import {
  IPE_SHEIN_GATE_PATH,
  IPE_SHEIN_BASE_PATH,
  SHEIN_WORKSPACE_SHOP_TYPE_KEY,
  parseSheinShopTypeSlugFromPath,
  shopTypeSlugForLabel,
  normalizeSheinShopTypeSlug,
} from '@/shein-platform/constants/sheinRoutes';
import { allowDocumentUnload } from '@/shein-platform/hooks/allowDocumentUnload';
import { normalizeSheinShopType } from '@/utils/platformShopCatalog';
import { installWorkspaceShopTypeRedirect } from '@/utils/workspaceShopTypeRedirect';

const SheinWorkspaceAuthContext = createContext(null);

function readStoredShopType() {
  try {
    return localStorage.getItem(SHEIN_WORKSPACE_SHOP_TYPE_KEY) || '';
  } catch {
    return '';
  }
}

function persistShopType(shopType) {
  try {
    const normalized = normalizeSheinShopType(shopType);
    if (normalized) {
      localStorage.setItem(SHEIN_WORKSPACE_SHOP_TYPE_KEY, normalized);
    } else {
      localStorage.removeItem(SHEIN_WORKSPACE_SHOP_TYPE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function SheinWorkspaceAuthProvider({ children }) {
  const [token, setToken] = useState(() => getSheinWorkspaceToken());
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [expiresAt, setExpiresAt] = useState(null);
  const [workspaceShopType, setWorkspaceShopType] = useState(() => readStoredShopType());

  const clearLocalSession = useCallback(() => {
    persistSheinWorkspaceToken(null);
    persistShopType('');
    setToken(null);
    setUnlocked(false);
    setExpiresAt(null);
    setWorkspaceShopType('');
  }, []);

  const refreshStatus = useCallback(async () => {
    if (!token) {
      setUnlocked(false);
      setExpiresAt(null);
      setChecking(false);
      return;
    }
    setChecking(true);
    try {
      const status = await sheinPlatformAPI.getSessionStatus();
      setUnlocked(Boolean(status?.unlocked));
      setExpiresAt(status?.expiresAt || null);
      const type = normalizeSheinShopType(status?.shopType) || readStoredShopType();
      if (type) {
        setWorkspaceShopType(type);
        persistShopType(type);
      }
    } catch {
      setUnlocked(false);
      setExpiresAt(null);
      persistSheinWorkspaceToken(null);
      setToken(null);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => installWorkspaceShopTypeRedirect({
    storageKey: SHEIN_WORKSPACE_SHOP_TYPE_KEY,
    basePath: IPE_SHEIN_BASE_PATH,
    parseSlugFromPath: parseSheinShopTypeSlugFromPath,
    shopTypeToSlug: (raw) => shopTypeSlugForLabel(raw) || normalizeSheinShopTypeSlug(raw),
  }), []);

  useEffect(() => {
    setSheinWorkspaceAuthRequiredHandler(() => {
      clearLocalSession();
      if (typeof window === 'undefined') return;
      const path = window.location.pathname || '';
      if (path.includes('/gate')) return;
      const redirect = `${path}${window.location.search || ''}`;
      const q = redirect && redirect !== IPE_SHEIN_GATE_PATH
        ? `?redirect=${encodeURIComponent(redirect)}`
        : '';
      // 工作区过期是应用主动整页跳转，不应被发品页未保存守卫拦截成「是否离开网站」
      allowDocumentUnload();
      window.location.replace(`${IPE_SHEIN_GATE_PATH}${q}`);
    });
    return () => setSheinWorkspaceAuthRequiredHandler(null);
  }, [clearLocalSession]);

  const unlock = useCallback(async (password, shopType) => {
    const normalizedType = normalizeSheinShopType(shopType) || '';
    const res = await sheinPlatformAPI.unlockSession(password, normalizedType || undefined);
    const nextToken = res?.token;
    if (!nextToken) {
      throw new Error('验证失败');
    }
    const boundType = normalizeSheinShopType(res?.shopType) || normalizedType;
    persistSheinWorkspaceToken(nextToken);
    persistShopType(boundType);
    setToken(nextToken);
    setUnlocked(true);
    setExpiresAt(res?.expiresAt || null);
    setWorkspaceShopType(boundType);
    return res;
  }, []);

  const lock = useCallback(async () => {
    try {
      await sheinPlatformAPI.lockSession();
    } catch {
      /* ignore */
    }
    clearLocalSession();
  }, [clearLocalSession]);

  const value = useMemo(
    () => ({
      unlocked,
      checking,
      expiresAt,
      workspaceShopType,
      unlock,
      lock,
      refreshStatus,
    }),
    [unlocked, checking, expiresAt, workspaceShopType, unlock, lock, refreshStatus],
  );

  return (
    <SheinWorkspaceAuthContext.Provider value={value}>
      {children}
    </SheinWorkspaceAuthContext.Provider>
  );
}

export function useSheinWorkspaceAuth() {
  const ctx = useContext(SheinWorkspaceAuthContext);
  if (!ctx) {
    throw new Error('useSheinWorkspaceAuth must be used within SheinWorkspaceAuthProvider');
  }
  return ctx;
}

export function useSheinWorkspaceAuthOptional() {
  return useContext(SheinWorkspaceAuthContext);
}
